// import PersonIcon from '@/atoms/icons/person.svg'
import { appSectionVar } from '@/apollo/cache'
import FactoringIcon from '@/atoms/icons/factoring-menu.svg'
import InvestmentsIcon from '@/atoms/icons/investments.svg'
import LoanBookIcon from '@/atoms/icons/loan-book.svg'
import RevenueIcon from '@/atoms/icons/revenue.svg'
import RiskManagementIcon from '@/atoms/icons/risk-management-menu.svg'
import SettingsIcon from '@/atoms/icons/settings.svg'
import { isProduction } from '@/helpers/config'
import { AppSections, UserDetailsInfo, UserRole } from '@/models/auth'

export const isPublicPath = (pathname: string) => {
  const publicPaths = ['/login', '/reset-password', '/change-password', '/verify-invoice', '/verify-outstanding-invoices']

  return publicPaths.some(path => pathname?.startsWith(path))
}
export const clientPath = {
  home: '/client/profile',
  contractDetails: '/client/profile/factoring-contract-details/:id',
  loanBookContractDetails: '/client/profile/loan-book-contract/:id',
  debtors: '/client/debtors',
  debtorDetails: '/client/debtors/:id',
  clientCreditNoteDetails: '/clients/debtors/client-credit-note/:id',
  notes: '/client/notes',
  invoices: '/client/invoices',
  invoiceDetails: '/client/invoices/:id',
  users: '/client/users',
  reports: '/client/reports',
  settlements: '/client/settlements',
  settlementDetails: '/client/settlements/:id',
  loanBookPayouts: '/client/loan-book-payouts',
  loanBookPayoutDetails: '/client/loan-book-payouts/:id',
  default: '/client/debtors',
}

export const path = {
  // Risk managements
  prospects: '/prospects',
  prospectDetails: '/prospects/details/:id',
  prospectDebtorDetails: '/prospects/prospectDebtor/:id',
  loginPage: '/login',
  resetPassword: '/reset-password',
  changePassword: '/change-password/:token',
  kycDetails: '/prospects/kyc/:id',
  reports: '/reports',
  stopList: '/stop-list',
  stopListPersonDetails: '/stop-list/person-details/:id',
  stopListCompanyDetails: '/stop-list/company-details/:id',
  annualReview: '/annual-review',
  annualReviewDetails: '/annual-review/client-details/:id',
  annualReviewDebtorDetails: '/annual-review/client-debtor/:id',
  annualReviewKycDetails: '/annual-review/kyc/:id',
  annualReviewContractDetails: '/annual-review/client-factoring-contract/:id',
  limitIncrease: '/limit-increase',
  limitIncreaseDetails: '/limit-increase/client-details/:id',
  limitIncreaseDebtorDetails: '/limit-increase/client-debtor/:id',
  limitIncreaseHistoryDetails: '/limit-increase/client-details/history/:id',
  limitIncreaseDebtorHistoryDetails: '/limit-increase/debtor-details/history/:id',
  prospectContractDetails: '/prospect-contract/:id',
  riskMonitoringTasks: '/risk-management/monitoring-tasks',
  riskMonitoringTaskDetails: '/risk-management/monitoring-tasks/:id',

  // System preferences
  system: '/system',
  reporting: '/reporting',
  userDetails: '/system/users/:id',
  brokerDetails: '/system/brokers/:id',
  debtorGroupDetails: '/system/debtor-group/:id',
  // Factoring
  clients: '/clients',
  clientsDetails: '/clients/:id',
  clientDebtorDetails: '/clients/client-debtor/:id',
  clientSettlementDetails: '/clients/client-settlement/:id',
  clientCreditNoteDetails: '/clients/client-debtor/client-credit-note/:id',
  factoringContractDetails: '/clients/client-factoring-contract/:id',
  loanBookContractDetails: '/clients/loan-book-contract/:id',
  debtors: '/debtors',
  debtorDetails: '/debtors/:id',
  invoices: '/invoices',
  invoiceDetails: '/invoices/:id',
  debtCollection: '/debt-collection',
  debtCollectionDetails: '/debt-collection/:id',
  clientRequirements: '/factoring/client-requirements',
  verifyOutstandingAmount: '/verify-outstanding-invoices/:id',
  paymentReminder: '/payment-reminder/:id',
  notes: '/notes',
  outstandingVerificationTasks: '/factoring/outstanding-verification-tasks',
  outstandingVerificationTaskDetails: '/factoring/outstanding-verification-tasks/:id',
  bulkVerificationTasks: '/factoring/bulk-verification-tasks',
  bulkVerificationTaskDetails: '/factoring/bulk-verification-tasks/:id',
  aiTasks: '/factoring/ai-tasks',
  aiTaskDetails: '/factoring/ai-tasks/:id',
  aiTaskDocumentDetails: '/factoring/ai-tasks/:id/documents/:fileId',
  //Payments
  bankStatements: '/bank-statements',
  payouts: '/payouts',
  factoringPayoutDetails: '/payouts/factoring/:id',
  loanBookPayoutDetails: '/payouts/loan-book/payout/:id',
  investmentPayoutDetails: '/payouts/investment/payout/:id',
  bankStatementDetails: '/bank-statements/:id',
  bankTransactionDetails: '/bank-statements/bank-transaction/:id',
  generalLedgers: '/general-ledgers',
  paymentsReports: '/payments-reports',
  generalLedgerReconciliations: '/general-ledgers/:id',
  generalLedgerReconciliationInvestigationDetails: '/general-ledgers/investigation/:id',
  verifyInvoice: '/verify-invoice/:id',
  // Loan book
  loans: '/loan-book/loans',
  loanDetails: '/loan-book/loans/:id',
  creditLines: '/loan-book/credit-lines',
  creditLineDetails: '/loan-book/credit-lines/:id',
  reverseFactoring: '/loan-book/reverse-factoring',
  reverseFactoringDetails: '/loan-book/reverse-factoring/:id',
  loanBookNotes: '/loan-book/notes',
  loanBookReports: '/loan-book/reports',
  loanBookDebtCollection: '/loan-book/debt-collection',
  loanBookDebtCollectionDetails: '/loan-book/debt-collection/:id',
  // Investments
  offers: '/investment/offers',
  offerDetails: '/investment/offers/:id',
  investors: '/investment/investors',
  investorDetails: '/investment/investors/:id',
  investmentPlanDetails: '/investment/investors/investment-plan/:id',
  investorKYCDetails: '/investment/investors/kyc/:id',
  investmentPlans: '/investment/investment-plans',
  investmentTaskManager: '/investment/task-manager',
  investmentTaskDetails: '/investment/task-manager/:id',
  reinvestmentDetails: '/investment/task-manager/reinvestment/:id',
  investmentNotes: '/investment/notes',
}

export const isOnlyInvestorManager = (user: UserDetailsInfo) => {
  const {
    investorManager,
    board,
    operations,
    operationsLead,
    riskLocal,
    riskLocalLead,
    headOfRisk,
    sales,
    salesLead,
    administrator,
    debtCollectorLead,
    debtCollectorOfficer,
  } = user

  return (
    investorManager &&
    !board &&
    !operations &&
    !operationsLead &&
    !riskLocal &&
    !riskLocalLead &&
    !headOfRisk &&
    !sales &&
    !salesLead &&
    !administrator &&
    !debtCollectorLead &&
    !debtCollectorOfficer
  )
}

export const isOnlySales = (user: UserDetailsInfo) => {
  const {
    investorManager,
    board,
    operations,
    operationsLead,
    riskLocal,
    riskLocalLead,
    headOfRisk,
    sales,
    salesLead,
    administrator,
    debtCollectorLead,
    debtCollectorOfficer,
  } = user

  return (
    !investorManager &&
    !board &&
    !operations &&
    !operationsLead &&
    !riskLocal &&
    !riskLocalLead &&
    !headOfRisk &&
    sales &&
    salesLead &&
    !administrator &&
    !debtCollectorLead &&
    !debtCollectorOfficer
  )
}

export const isOnlySalesDCAdministrator = (user: UserDetailsInfo) => {
  const {
    sales,
    salesLead,
    administrator,
    debtCollectorLead,
    debtCollectorOfficer,
    board,
    operations,
    operationsLead,
    riskLocal,
    riskLocalLead,
    headOfRisk,
  } = user

  return (
    (sales || salesLead || administrator || debtCollectorLead || debtCollectorOfficer) &&
    !board &&
    !operations &&
    !operationsLead &&
    !riskLocal &&
    !riskLocalLead &&
    !headOfRisk
  )
}

export const appSections = (user?: UserDetailsInfo) => {
  const onlyInvestorManager = user ? isOnlyInvestorManager(user) : false
  const onlySalesDCAdministrator = user ? isOnlySalesDCAdministrator(user) : false

  return [
    {
      name: AppSections.RISK_MANAGEMENT,
      label: `app_switcher.menu.RISK_MANAGEMENT`,
      icon: RiskManagementIcon,
      defaultUrl: path.prospects,
      hide: onlyInvestorManager,
    },
    {
      name: AppSections.FACTORING,
      label: `app_switcher.menu.FACTORING`,
      icon: FactoringIcon,
      defaultUrl: path.invoices,
      hide: onlyInvestorManager,
    },
    {
      name: AppSections.LOAN_BOOK,
      label: `app_switcher.menu.LOAN_BOOK`,
      icon: LoanBookIcon,
      defaultUrl: path.loans,
      hide: onlyInvestorManager,
    },
    {
      name: AppSections.PAYMENTS,
      label: `app_switcher.menu.PAYMENTS`,
      icon: RevenueIcon,
      hide: user ? !(user.operations || user.board) : false,
      defaultUrl: path.bankStatements,
    },
    {
      name: AppSections.ADMINISTRATION,
      label: `app_switcher.menu.ADMINISTRATION`,
      icon: SettingsIcon,
      defaultUrl: path.system,
      hide: onlyInvestorManager,
    },
    {
      name: AppSections.INVESTMENT,
      label: `app_switcher.menu.INVESTMENT`,
      icon: InvestmentsIcon,
      hide: isProduction || onlySalesDCAdministrator,
      defaultUrl: path.investors,
    },
  ]
}

export const getAppSectionName = (path: string) => {
  if (['/loan-book/'].some(text => path.includes(text))) return AppSections.LOAN_BOOK
  if (['/investment/'].some(text => path.includes(text))) return AppSections.INVESTMENT
  if (['/clients', '/debtors', '/invoices', '/debt-collection', '/factoring', '/notes'].some(text => path.includes(text)))
    return AppSections.FACTORING
  if (['/system', '/reporting'].some(text => path.includes(text))) return AppSections.ADMINISTRATION
  if (['/bank-statements', '/general-ledgers', '/payments-reports', '/payouts'].some(text => path.includes(text)))
    return AppSections.PAYMENTS
  if (
    ['/prospects', '/reports', '/stop-list', '/annual-review', '/limit-increase', '/prospect-contract/', '/risk-management/'].some(text =>
      path.includes(text),
    )
  )
    return AppSections.RISK_MANAGEMENT

  return null
}

export const getDefaultUrl = (role: UserRole) =>
  role === UserRole.BACK_OFFICE_USER ? appSections().find(({ name }) => name === appSectionVar()).defaultUrl : clientPath.default
